import { SelectOption } from '~/shared/utils/types';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  createAutoscalingStateFilters,
  createEligibilityFilters,
} from '~/shared/utils/autoscalingFilterUtils';

export {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  createAutoscalingStateFilters,
  createEligibilityFilters,
};

export type FilterStoredInURL = Omit<SelectOption, 'group'> & {
  group: SelectOption['group'];
};

export function createCollapsedAutoscalingStateFilters(stateIds: string[]): SelectOption[] {
  const mapped = createAutoscalingStateFilters(stateIds);

  if (mapped.length === 0) {
    return [];
  }

  const hasOn = mapped.some((m) => m.id === 'on');
  const hasOff = mapped.some((m) => m.id === 'off');

  // Collapse on+off into one synthetic filter so getApiFilters emits {field: 'autoscalingState', value: 'on,off', op: 'equals'}
  if (hasOn && hasOff) {
    return [{ id: 'on,off', name: 'on,off', group: 'autoscalingState' }];
  }

  return mapped;
}

export function filterNonSpecificGroups(filters: FilterStoredInURL[]): SelectOption[] {
  return filters.filter((f) => {
    const isTagGroup = typeof f.group === 'string' && f.group.startsWith('tag:');
    return (
      f.group !== 'volumeId' &&
      f.group !== 'instanceId' &&
      f.group !== 'clusterName' &&
      f.group !== 'accountId' &&
      f.group !== 'autoscalingState' &&
      f.group !== 'supported' &&
      !isTagGroup
    );
  });
}

export function combineFilters(
  baseApiFilters: SelectOption[],
  pendingClusterNameFilters: SelectOption[],
  pendingVolumeIdFilters: SelectOption[],
  pendingVolumeTagFilters: SelectOption[],
  pendingAccountFilters: SelectOption[],
  pendingInstanceFilters: SelectOption[],
  autoscalingStateFilters: SelectOption[],
  eligibilityFilters: SelectOption[]
): SelectOption[] {
  return [
    ...baseApiFilters,
    ...pendingClusterNameFilters,
    ...pendingVolumeIdFilters,
    ...pendingVolumeTagFilters,
    ...pendingAccountFilters,
    ...pendingInstanceFilters,
    ...autoscalingStateFilters,
    ...eligibilityFilters,
  ];
}

export function buildAutoscalingApiFilters(
  stateIds: string[],
  eligibilityIds: string[]
): SelectOption[] {
  return [
    ...createCollapsedAutoscalingStateFilters(stateIds),
    ...createEligibilityFilters(eligibilityIds),
  ];
}
